import React, { useState } from 'react';

import { APIURL } from '../../../configApi/apiConfig';
import axios from '../../../configApi/axiosConfig';

const EMPTY_FORM = { descripcion: '', notaInterna: '' };

export const Ticket = () => {
    const [formData, setFormData] = useState(EMPTY_FORM);
    const [formErrors, setFormErrors] = useState({});
    const [successMsg, setSuccessMsg] = useState('');
    const [loading, setLoading] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        if (formErrors[name]) {
            setFormErrors((prev) => {
                const next = { ...prev };
                delete next[name];
                return next;
            });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const errors = {};

        const descripcionTrimmed = formData.descripcion.trim();
        const notaInternaTrimmed = formData.notaInterna.trim();

        if (!descripcionTrimmed) {
            errors.descripcion = 'La descripción es obligatoria';
        } else if (descripcionTrimmed.length < 10) {
            errors.descripcion = 'La descripción debe tener al menos 10 caracteres';
        }

        if (notaInternaTrimmed && notaInternaTrimmed.length < 10) {
            errors.notaInterna = 'La nota interna debe tener al menos 10 caracteres si se ingresa';
        }

        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            return;
        }

        const payload = {
            Descripcion: descripcionTrimmed,
            RespuestaSolucion: notaInternaTrimmed || '',
        };

        try {
            setLoading(true);
            const url = APIURL.post_ticket_new();
            await axios.post(url, payload);
            setFormData(EMPTY_FORM);
            setFormErrors({});
            setSuccessMsg('¡Ticket registrado exitosamente! El área de Desarrollo ha recibido tu solicitud.');
            setTimeout(() => setSuccessMsg(''), 4000);
        } catch (error) {
            setFormErrors({ general: 'Error al registrar el ticket. Intenta nuevamente.' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-50 p-4 md:p-6 lg:p-8">
            <div className="max-w-2xl mx-auto space-y-4">

                {/* Encabezado */}
                <div className="bg-blue-700 text-white rounded-2xl px-6 py-5 shadow-md">
                    <div className="flex items-center gap-3">
                        <svg className="w-8 h-8 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 4H7a2 2 0 01-2-2V6a2 2 0 012-2h5l5 5v11a2 2 0 01-2 2z" />
                        </svg>
                        <div>
                            <h1 className="text-lg font-bold leading-tight">Sistema de Tickets — Área de Desarrollo</h1>
                            <p className="text-blue-200 text-xs mt-0.5">
                                Registra requerimientos, incidencias o solicitudes para el equipo de Desarrollo de Software.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Mensaje de éxito */}
                {successMsg && (
                    <div className="flex items-start gap-2 bg-green-50 border border-green-300 text-green-700 text-sm rounded-lg px-4 py-3">
                        <svg className="w-4 h-4 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        {successMsg}
                    </div>
                )}

                {/* Error general */}
                {formErrors.general && (
                    <div className="flex items-start gap-2 bg-red-50 border border-red-300 text-red-700 text-sm rounded-lg px-4 py-3">
                        <svg className="w-4 h-4 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm-1-9a1 1 0 112 0v4a1 1 0 11-2 0V9zm1-4a1 1 0 100 2 1 1 0 000-2z" clipRule="evenodd" />
                        </svg>
                        {formErrors.general}
                    </div>
                )}

                {/* Formulario */}
                <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-md p-6 space-y-5">

                    {/* Descripción */}
                    <div className="flex flex-col gap-1">
                        <label className="text-sm font-medium text-slate-600" htmlFor="descripcion">
                            Descripción <span className="text-red-500">*</span>
                        </label>
                        <p className="text-xs text-slate-400">Detalla el problema, requerimiento o solicitud. Mínimo 10 caracteres.</p>
                        <textarea
                            id="descripcion"
                            name="descripcion"
                            value={formData.descripcion}
                            onChange={handleChange}
                            rows={5}
                            maxLength={8000}
                            placeholder="Ej: Al guardar el formulario de clientes se muestra el error 500 en producción desde las 08h00..."
                            className={`w-full rounded-lg border px-3 py-2 text-sm text-slate-700 resize-y uppercase focus:outline-none focus:ring-2 focus:ring-blue-400 ${formErrors.descripcion ? 'border-red-400 bg-red-50' : 'border-slate-300'
                                }`}
                        />
                        <div className="flex justify-between items-center">
                            {formErrors.descripcion
                                ? <span className="text-xs text-red-500">{formErrors.descripcion}</span>
                                : <span />
                            }
                            <span className="text-xs text-slate-400">{formData.descripcion.length} / 8000</span>
                        </div>
                    </div>

                    {/* Nota Interna */}
                    <div className="flex flex-col gap-1">
                        <label className="text-sm font-medium text-slate-600" htmlFor="notaInterna">
                            Nota Interna <span className="text-slate-400 text-xs font-normal">(opcional)</span>
                        </label>
                        <p className="text-xs text-slate-400">Información adicional visible solo para el equipo interno. Si se ingresa, mínimo 10 caracteres.</p>
                        <textarea
                            id="notaInterna"
                            name="notaInterna"
                            value={formData.notaInterna}
                            onChange={handleChange}
                            rows={4}
                            maxLength={8000}
                            placeholder="Ej: El cliente reportó que el error ocurre únicamente con cédulas extranjeras..."
                            className={`w-full rounded-lg border px-3 py-2 text-sm text-slate-700 resize-y focus:outline-none focus:ring-2 uppercase focus:ring-blue-400 ${formErrors.notaInterna ? 'border-red-400 bg-red-50' : 'border-slate-300'
                                }`}
                        />
                        <div className="flex justify-between items-center">
                            {formErrors.notaInterna
                                ? <span className="text-xs text-red-500">{formErrors.notaInterna}</span>
                                : <span />
                            }
                            <span className="text-xs text-slate-400">{formData.notaInterna.length} / 8000</span>
                        </div>
                    </div>

                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-blue-700 hover:bg-blue-800 disabled:opacity-60 disabled:cursor-not-allowed text-white font-medium py-2.5 rounded-lg transition-colors text-sm tracking-wide flex items-center justify-center gap-2"
                    >
                        {loading && (
                            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                            </svg>
                        )}
                        {loading ? 'Registrando...' : 'Registrar Ticket'}
                    </button>
                </form>
            </div>
        </div>
    );
};


export default Ticket;

